import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { StageBadge, PriorityBadge, StatusBadge } from "@/components/Badges";
import { MediaThumb } from "@/components/MediaThumb";
import { STAGES, DEPARTMENTS, PRIORITIES, deptLabel } from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  Phone,
  User,
  Building2,
  Calendar,
  Flag,
  Plus,
  Upload,
  X,
  Lock,
  Unlock,
  Pencil,
  Trash2,
  Loader2,
  UserCheck,
  Clock,
  FileDown,
} from "lucide-react";
import { format } from "date-fns";

export default function ComplaintDetailsPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const canManage = user?.role === "admin" || user?.role === "officer";
  const canDelete = user?.role === "admin";
  const isClosed = c?.status === "Closed";

  async function refresh() {
    setLoading(true);
    try {
      const [cRes, uRes] = await Promise.all([
        api.get(`/complaints/${id}`),
        api.get(`/complaints/${id}/updates`),
      ]);
      setC(cRes.data);
      setUpdates(uRes.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function closeC() {
    try {
      await api.post(`/complaints/${id}/close`);
      toast.success("Complaint closed");
      refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function reopenC() {
    try {
      await api.post(`/complaints/${id}/reopen`);
      toast.success("Complaint reopened");
      refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function deleteC() {
    try {
      await api.delete(`/complaints/${id}`);
      toast.success("Complaint deleted");
      navigate("/complaints");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  const [exportingPdf, setExportingPdf] = useState(false);
  async function exportPdf() {
    setExportingPdf(true);
    try {
      const res = await api.get(`/complaints/${id}/export.pdf`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `complaint_${c?.complaint_no || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("Report downloaded");
    } catch (_e) {
      toast.error("PDF export failed");
    } finally {
      setExportingPdf(false);
    }
  }

  if (loading || !c) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="complaint-details">
      <button
        onClick={() => navigate(-1)}
        data-testid="details-back"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" /> Back to complaints
      </button>

      {/* Header card */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-2">
              Complaint · <span className="font-mono-cms tracking-normal">{c.complaint_no}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight break-words">
              {c.subject}
            </h1>
            <div className="mt-3 flex items-center flex-wrap gap-2">
              <StatusBadge status={c.status} />
              <StageBadge stage={c.current_stage} />
              <PriorityBadge priority={c.priority} />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              data-testid="export-pdf-btn"
              onClick={exportPdf}
              disabled={exportingPdf}
            >
              {exportingPdf ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <FileDown className="w-4 h-4 mr-1.5" />
              )}
              Export PDF
            </Button>
            {canManage && !isClosed && (
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="add-update-btn"
                    className="bg-orange-600 hover:bg-orange-700 text-white font-semibold"
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> Add Update
                  </Button>
                </DialogTrigger>
                <AddUpdateDialog
                  complaintId={id}
                  currentDept={c.department}
                  onDone={() => {
                    setAddOpen(false);
                    refresh();
                  }}
                />
              </Dialog>
            )}
            {canManage && !isClosed && (
              <Button
                variant="outline"
                data-testid="edit-btn"
                onClick={() => setEditing(true)}
              >
                <Pencil className="w-4 h-4 mr-1.5" /> Edit
              </Button>
            )}
            {canManage && !isClosed && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="close-btn">
                    <Lock className="w-4 h-4 mr-1.5" /> Close
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Close this complaint?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Closed complaints become read-only. You can reopen them later.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={closeC}
                      data-testid="close-confirm"
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      Yes, close
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {canManage && isClosed && (
              <Button
                data-testid="reopen-btn"
                onClick={reopenC}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                <Unlock className="w-4 h-4 mr-1.5" /> Reopen
              </Button>
            )}
            {canDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="delete-btn" className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete complaint permanently?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will erase the complaint and its entire timeline. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={deleteC}
                      data-testid="delete-confirm"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Meta grid */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-6 pt-6 border-t border-slate-100">
          <Meta icon={User} label="Applicant" value={c.applicant_name} />
          <Meta
            icon={Phone}
            label="Mobile"
            value={
              <Link
                to={`/search?mobile=${c.mobile}`}
                className="font-mono-cms text-slate-900 hover:text-orange-600"
              >
                {c.mobile}
              </Link>
            }
          />
          <Meta
            icon={Building2}
            label="Department"
            value={<span className={lang === "hi" ? "font-devanagari" : ""}>{deptLabel(c.department, lang)}</span>}
          />
          <Meta
            icon={UserCheck}
            label="Assigned Officer"
            value={c.assigned_officer || <span className="text-slate-400">Unassigned</span>}
          />
          <Meta
            icon={Calendar}
            label="Filed on"
            value={format(new Date(c.created_at), "dd MMM yyyy · HH:mm")}
          />
          <Meta
            icon={Clock}
            label="Last updated"
            value={format(new Date(c.updated_at), "dd MMM yyyy · HH:mm")}
          />
          <Meta
            icon={Flag}
            label="Next action by"
            value={
              c.expected_next_action_date
                ? format(new Date(c.expected_next_action_date), "dd MMM yyyy")
                : <span className="text-slate-400">Not set</span>
            }
          />
          <Meta
            icon={User}
            label="Filed by"
            value={c.created_by?.name}
            sub={c.created_by?.role}
          />
        </div>

        <div className="mt-6 pt-6 border-t border-slate-100">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Description
          </div>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
            {c.description}
          </p>
        </div>
      </div>

      {editing && (
        <EditDialog
          complaint={c}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            refresh();
          }}
        />
      )}

      {/* Timeline */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">
              Complaint Timeline
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {updates.length} entries · latest first
            </p>
          </div>
        </div>
        <div className="relative border-l-2 border-slate-200 ml-4 py-2">
          {updates.map((u, idx) => (
            <div
              key={u.id}
              data-testid={`timeline-item-${u.update_number}`}
              className="mb-8 ml-8 relative timeline-item"
              style={{ animationDelay: `${idx * 40}ms` }}
            >
              <TimelineDot stage={u.current_stage} />
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-[11px] font-mono-cms uppercase tracking-wider font-bold text-slate-500">
                        Update #{u.update_number}
                      </span>
                      <span className="text-slate-300">·</span>
                      <StageBadge stage={u.current_stage} />
                      {u.kind === "closed" && (
                        <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider">
                          Closure
                        </span>
                      )}
                    </div>
                    <h3 className="text-base font-bold text-slate-900 leading-snug">
                      {u.title}
                    </h3>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <div className="font-mono-cms">
                      {format(new Date(u.created_at), "dd MMM yyyy")}
                    </div>
                    <div className="font-mono-cms">
                      {format(new Date(u.created_at), "HH:mm")}
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {u.description}
                </p>
                <div className="mt-3 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {u.updated_by?.name}
                    <span className="text-slate-400 uppercase text-[10px] font-bold">
                      · {u.updated_by?.role}
                    </span>
                  </span>
                  {u.department && (
                    <span className={`inline-flex items-center gap-1 ${lang === "hi" ? "font-devanagari" : ""}`}>
                      <Building2 className="w-3 h-3" />
                      {deptLabel(u.department, lang)}
                    </span>
                  )}
                  {u.expected_next_action_date && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Next by{" "}
                      {format(new Date(u.expected_next_action_date), "dd MMM yyyy")}
                    </span>
                  )}
                </div>
                {u.media && u.media.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {u.media.map((m) => (
                      <MediaThumb key={m.id} media={m} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {updates.length === 0 && (
            <div className="ml-4 text-sm text-slate-500 py-6">
              No timeline entries yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ icon: Icon, label, value, sub }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-1.5 text-sm font-semibold text-slate-900">
        {value}
      </div>
      {sub && (
        <div className="text-[11px] text-slate-500 uppercase font-mono-cms">{sub}</div>
      )}
    </div>
  );
}

function TimelineDot({ stage }) {
  const colors = {
    Received: "#4f46e5",
    "Under Review": "#2563eb",
    Forwarded: "#0d9488",
    "In Progress": "#d97706",
    "Field Inspection": "#7c3aed",
    "Action Taken": "#059669",
    Pending: "#e11d48",
    Resolved: "#16a34a",
    Closed: "#475569",
  };
  const c = colors[stage] || "#475569";
  return (
    <span
      className="absolute -left-[42px] top-2 h-4 w-4 rounded-full ring-4 ring-white"
      style={{ backgroundColor: c, boxShadow: `0 0 0 2px ${c}44` }}
    />
  );
}

function AddUpdateDialog({ complaintId, currentDept, onDone }) {
  const { lang } = useLang();
  const [form, setForm] = useState({
    title: "",
    description: "",
    current_stage: "In Progress",
    department: currentDept,
    expected_next_action_date: "",
  });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  async function onFilePick(e) {
    const picks = Array.from(e.target.files || []);
    if (!picks.length) return;
    for (const f of picks) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [...prev, data]);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      }
    }
    e.target.value = "";
  }

  async function submit() {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.expected_next_action_date) delete payload.expected_next_action_date;
      const { data: upd } = await api.post(
        `/complaints/${complaintId}/updates`,
        payload
      );
      // attach files
      for (const f of files) {
        const fd = new FormData();
        fd.append("file_id", f.id);
        await api.post(`/complaints/${complaintId}/updates/${upd.id}/media`, fd);
      }
      toast.success("Update added");
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Add a new update</DialogTitle>
        <DialogDescription>
          Log an action, forward the complaint, or attach supporting evidence.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="text-sm font-semibold">Title *</Label>
          <Input
            data-testid="upd-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-1.5 h-11"
            placeholder="e.g. Forwarded to Sub-Divisional Officer"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold">Description *</Label>
          <Textarea
            data-testid="upd-desc"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={4}
            className="mt-1.5"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-semibold">Current Stage *</Label>
            <Select
              value={form.current_stage}
              onValueChange={(v) => setForm({ ...form, current_stage: v })}
            >
              <SelectTrigger data-testid="upd-stage" className="mt-1.5 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-semibold">Department</Label>
            <Select
              value={form.department}
              onValueChange={(v) => setForm({ ...form, department: v })}
            >
              <SelectTrigger data-testid="upd-dept" className={`mt-1.5 h-11 ${lang === "hi" ? "font-devanagari" : ""}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {DEPARTMENTS.map((d) => (
                  <SelectItem key={d} value={d} className={lang === "hi" ? "font-devanagari" : ""}>
                    {deptLabel(d, lang)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold">Expected Next Action Date</Label>
          <Input
            type="date"
            data-testid="upd-next-date"
            value={form.expected_next_action_date}
            onChange={(e) =>
              setForm({ ...form, expected_next_action_date: e.target.value })
            }
            className="mt-1.5 h-11"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold">Attach Media</Label>
          <div className="mt-1.5 border-2 border-dashed border-slate-300 rounded-md p-4 text-center">
            <input
              type="file"
              id="upd-attach"
              data-testid="upd-attach"
              multiple
              onChange={onFilePick}
              className="hidden"
            />
            <label
              htmlFor="upd-attach"
              className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold text-slate-700"
            >
              <Upload className="w-4 h-4" /> Choose files
            </label>
          </div>
          {files.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {files.map((f) => (
                <div
                  key={f.id}
                  className="text-xs bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5 flex items-center justify-between"
                >
                  <span className="truncate">{f.original_filename}</span>
                  <button
                    onClick={() =>
                      setFiles((prev) => prev.filter((x) => x.id !== f.id))
                    }
                    className="text-slate-400 hover:text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={submit}
          data-testid="upd-submit"
          disabled={saving}
          className="bg-orange-600 hover:bg-orange-700 text-white font-semibold"
        >
          {saving ? "Saving..." : "Add Update"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function EditDialog({ complaint, onClose, onSaved }) {
  const { lang } = useLang();
  const [form, setForm] = useState({
    priority: complaint.priority,
    current_stage: complaint.current_stage,
    department: complaint.department,
    assigned_officer: complaint.assigned_officer || "",
    expected_next_action_date: complaint.expected_next_action_date || "",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.expected_next_action_date) payload.expected_next_action_date = null;
      if (!payload.assigned_officer) payload.assigned_officer = null;
      await api.patch(`/complaints/${complaint.id}`, payload);
      toast.success("Complaint updated");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit complaint</DialogTitle>
          <DialogDescription>
            Adjust stage, department, priority or assign an officer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-semibold">Current Stage</Label>
            <Select
              value={form.current_stage}
              onValueChange={(v) => setForm({ ...form, current_stage: v })}
            >
              <SelectTrigger data-testid="edit-stage" className="mt-1.5 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-semibold">Department</Label>
            <Select
              value={form.department}
              onValueChange={(v) => setForm({ ...form, department: v })}
            >
              <SelectTrigger data-testid="edit-dept" className={`mt-1.5 h-11 ${lang === "hi" ? "font-devanagari" : ""}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {DEPARTMENTS.map((d) => (
                  <SelectItem key={d} value={d} className={lang === "hi" ? "font-devanagari" : ""}>
                    {deptLabel(d, lang)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-semibold">Priority</Label>
            <Select
              value={form.priority}
              onValueChange={(v) => setForm({ ...form, priority: v })}
            >
              <SelectTrigger data-testid="edit-priority" className="mt-1.5 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-semibold">Assigned Officer</Label>
            <Input
              data-testid="edit-officer"
              value={form.assigned_officer}
              onChange={(e) =>
                setForm({ ...form, assigned_officer: e.target.value })
              }
              className="mt-1.5 h-11"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold">Expected Next Action</Label>
            <Input
              type="date"
              data-testid="edit-next-date"
              value={form.expected_next_action_date || ""}
              onChange={(e) =>
                setForm({ ...form, expected_next_action_date: e.target.value })
              }
              className="mt-1.5 h-11"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={save}
            data-testid="edit-save"
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
